/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

export type Order = 'asc' | 'desc';

export enum Currency {
    BYN = 'BYN',
    USD = 'USD',
    AZN = 'AZN',
}

export interface AddressResource {
    region: string;
    locality: string;
    city: string;
    postCode: string;
    street: string;
    building: string;
}

export interface ListResource<T> {
    data: T[];
    count: number;
}

export interface PageQueryParams {
    limit: number;
    offset: number;
}

export interface AfterKeyPageQueryParams {
    afterKey: string;
    limit?: number;
}

export interface SortQueryParams {
    sort?: string;
}

export interface SortQueryParamsGeneric<T> {
    sortType: T;
    sortOrder: Order;
}

export interface SearchQueryParams {
    search_type: string;
    search_text: string;
}

export interface PeriodQueryParams {
    dateFrom: string;
    dateTo: string;
}

export enum TurnoversByPeriodGroupType {
    YEAR = 'YEAR',
    MONTH = 'MONTH',
    DAY = 'DAY',
}

export interface TurnoversByPeriodQueryParams {
    group: TurnoversByPeriodGroupType;
    year?: string;
    month?: string;
}

export type TurnoversByObjectsQueryParams = PageQueryParams & PeriodQueryParams;

export type TurnoversByCashBoxesQueryParams = PageQueryParams & PeriodQueryParams;

export type TurnoversByPeriodEXCELParams = { year: string };

export type TurnoversByTaxEntityEXCELParams = PeriodQueryParams;

export interface RegistryTurnoversBase {
    sales: {
        count: number;
        sum: number;
    };
    corrections: {
        count: number;
        sum: number;
    };
    moneybacks: {
        count: number;
        sum: number;
    };
    rollbacks: {
        count: number;
        sum: number;
    };
    cash: number;
    cashless: number;
    bonus: number;
    prepayment: number;
    credit: number;
    vatSum: number;
    turnover: number;
    currency: string;
}

export interface RegistryTurnoversByPeriodDto extends RegistryTurnoversBase {
    date: string;
}

export interface RegistryTurnoversByCashBox extends RegistryTurnoversBase {
    taxNumber: string;
}

export interface RegistryTurnoversByObject extends RegistryTurnoversBase {
    taxNumber: string;
    name: string;
}

export interface TurnoversByPeriodWithParams {
    turnovers: RegistryTurnoversByPeriodDto[];
    params: TurnoversByPeriodQueryParams;
}

export interface DayTurnover {
    daySum: RegistryTurnoversByPeriodDto;
    day: string;
}

export interface MonthTurnover {
    monthSum: RegistryTurnoversByPeriodDto;
    month: string;
    isHiddenDaysList: boolean;
    daysTurnovers: DayTurnover[];
}

export interface YearTurnover {
    yearSum: RegistryTurnoversByPeriodDto;
    year: string;
    isHiddenMonthsList: boolean;
    monthsTurnovers: MonthTurnover[];
}

export interface EntirePeriodTurnover {
    yearsTurnovers: YearTurnover[];
}

export interface RegistryTurnoversByCashBoxesDto {
    count: number;
    turnovers: RegistryTurnoversByCashBox[];
}

export interface RegistryTurnoversByObjectsDto {
    count: number;
    turnovers: RegistryTurnoversByObject[];
}

export interface PageParams {
    pageSize: number;
    page: number;
}

export enum AiActivitiesType {
    ALCOHOL_TOBACCO = 'ALCOHOL_TOBACCO',
    APPLIANCES = 'APPLIANCES',
    CLOTHES_SHOES = 'CLOTHES_SHOES',
    COOKEDFOOD = 'COOKEDFOOD',
    COSMETICS = 'COSMETICS',
    FLOWERS = 'FLOWERS',
    FUELS = 'FUELS',
    FURNITURE = 'FURNITURE',
    GROCERY = 'GROCERY',
    HOUSEWARES = 'HOUSEWARES',
    JEWELRY_ACCESSORIES = 'JEWELRY_ACCESSORIES',
    MISCELLANEOUS_GOODS = 'MISCELLANEOUS_GOODS',
    PETS = 'PETS',
    PHARMACY_OPTICS = 'PHARMACY_OPTICS',
    SANITARY_BUILDING_MATERIAL = 'SANITARY_BUILDING_MATERIAL',
    SERVICE = 'SERVICE',
    SPORT = 'SPORT',
    TOYS_BOOKS_GAMES_MOVIES_STATIONERY = 'TOYS_BOOKS_GAMES_MOVIES_STATIONERY', //TODO remove, left just for compatibility
    TOYS_BOOKS_GAMES_STATIONERY = 'TOYS_BOOKS_GAMES_STATIONERY',
    UNIDENTIFIABLE = 'UNIDENTIFIABLE',
    ALL_TYPES = 'ALL_TYPES',
}

export interface AiClassificationHistoryItemDto {
    insertedDateAtUtc: string;
    startDateAtUtc: string;
    endDateAtUtc: string;
    classification: AiActivitiesType;
    classificationProbability: number;
}

export interface AiClassificationsHistoryDto {
    count: number;
    items: AiClassificationHistoryItemDto[];
}

export enum AiCategoriesType {
    CLOTHESSHOES = 'CLOTHESSHOES',
    FUELS = 'FUELS',
    SERVICE = 'SERVICE',
    FURNITURE = 'FURNITURE',
    APPLIANCES = 'APPLIANCES',
    COOKEDFOOD = 'COOKEDFOOD',
    COSMETIC = 'COSMETIC',
    FLOWERS = 'FLOWERS',
    CUTLERY = 'CUTLERY',
    MEDICINE = 'MEDICINE',
    INTIMATEGOODS = 'INTIMATEGOODS',
    CHILDRENGOODS = 'CHILDRENGOODS',
    PLUMBING = 'PLUMBING',
    CONSTRUCTIONMATERIALS = 'CONSTRUCTIONMATERIALS',
    TOBACCO = 'TOBACCO',
    ALCOHOL = 'ALCOHOL',
    BAKERYPRODUCTS = 'BAKERYPRODUCTS',
    SNACKS = 'SNACKS',
    DRINKS = 'DRINKS',
    FRESHFRUITS = 'FRESHFRUITS',
    DRIEDFRUITVEGETABLESNUTS = 'DRIEDFRUITVEGETABLESNUTS',
    GROCERIES = 'GROCERIES',
    CEREALSLEGUMES = 'CEREALSLEGUMES',
    CANNEDFOOD = 'CANNEDFOOD',
    MEAT = 'MEAT',
    SEARIVERPRODUCTS = 'SEARIVERPRODUCTS',
    SOURDAIRY = 'SOURDAIRY',
    DAIRY = 'DAIRY',
    FATS = 'FATS',
    EGGS = 'EGGS',
    VEGETABLES = 'VEGETABLES',
    CONFECTIONERY = 'CONFECTIONERY',
    PACKAGING = 'PACKAGING',
    BOOKSFILMSCOMPUTERGAMES = 'BOOKSFILMSCOMPUTERGAMES', //TODO remove, left just for compatibility
    BOOKSCOMPUTERGAMES = 'BOOKSCOMPUTERGAMES',
    STATIONERY = 'STATIONERY',
    GAMESTOYS = 'GAMESTOYS',
    PETS = 'PETS',
    SPORT = 'SPORT',
    DECORATION = 'DECORATION',
    OTHER_GOODS = 'OTHER_GOODS',
    HOMECHEMISTRY = 'HOMECHEMISTRY',
    PERSONALHYGIENE = 'PERSONALHYGIENE',
    BATTERIES = 'BATTERIES',
    INDIVIDUALPROTECTIONMEANS = 'INDIVIDUALPROTECTIONMEANS',
    HOUSEHOLDGOODS = 'HOUSEHOLDGOODS',
    AUTOMOTIVEGOODS = 'AUTOMOTIVEGOODS',
    COUNTRYHOUSEGOODS = 'COUNTRYHOUSEGOODS',
    TOURISM = 'TOURISM',
    ECIGARETTES = 'ECIGARETTES',
    TRASH = 'TRASH',
    DRUGS = 'DRUGS',
    ENERGY_DRINKS = 'ENERGY_DRINKS',
    LEATHERFUR = 'LEATHERFUR',
    VAT_FREE = 'VAT_FREE',
}

export enum AiModels {
    MAIN = 'MAIN',
    VAT_FREE = 'VAT_FREE',
    GROUPS = 'GROUPS',
}
